import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import {
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Stack,
  Typography,
  Card,
  Chip
} from "@mui/material";
import loadingimg from "../Assets/images/loading image 1.png";
import usePrescriptionStore from "../DataRepository/Prescription";
import { ChevronLeft } from "@mui/icons-material";
import { profileModel, prescriptionModel } from "../Axios/AxiosConfig";
import DeleteIcon from '@mui/icons-material/Delete';

function Prescription() {

  const { setPrescription, resetPrescriptionState, selectedPatientdetails, selectedAppointemntdetails } = usePrescriptionStore();

  const [prescriptions, setPrescriptions] = useState([]);

  const [loading, setLoading] = useState(false);

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);

  const [draftToDelete, setDraftToDelete] = useState(null);

  const navigate = useNavigate();

  const doctorDetailsCookie = Cookies.get("Doctor_details");

  let doctorDetails = {};

  if (doctorDetailsCookie) {
    try {
      doctorDetails = JSON.parse(doctorDetailsCookie);
    } catch (error) {
      console.error("Error parsing Doctor_details cookie:", error);
    }
  }

  const getPrescription = async () => {
    try {
      setLoading(true);
      const res = await profileModel.post("/getPrescriptionforDoctor", {
        PatientID: selectedPatientdetails?.UID,
      });
      if (res.status === 200) {
        setPrescriptions(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPrescription();
  }, []);

  const handleNavigate = (data) => {
    setPrescription(data);
    navigate(data.draftStatus ? "/prescription" : "/ViewPrescription");
  };

  const filteredPrescriptions = prescriptions.filter((prescription) => {
    if (prescription.draftStatus) {
      return (
        prescription.Appointment_details?.doctor_name ===
        doctorDetails.name
      );
    }
    return true;
  });

  const uniquePrescriptions = [
    ...new Set(filteredPrescriptions.map((p) => p.id)),
  ].map((id) => filteredPrescriptions.find((p) => p.id === id));


  const deleteDraftById = async (id, PatientID, AppointmentID) => {
    try {

      setLoading(true);

      const res = await prescriptionModel.delete("/DeletePrescription", {
        params: { id, PatientID, AppointmentID }
      });

      if (res.status === 200) {
        setOpenDialog(false);
        setDraftToDelete(null);
        setShouldRefetch(prev => !prev);
        getPrescription()
      }

    } catch (err) {
      console.error("Error deleting draft:", err);
    } finally {
      setLoading(false);
    }
  };

  function convertToISO(dateStr) {
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f8f9fa',
      width: '100%',
      position: 'relative'
    }}>
      {/* Sticky Header */}
      <Box sx={{ p: 2, backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', zIndex: 10 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate(`/Patient/${selectedAppointemntdetails?.Appointment_id}`)} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: '#3B362A' }}>
            Prescriptions
          </Typography>
        </Stack>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{
        p: 2,
        pb: 12, // Space for bottom button
      }}>
        {uniquePrescriptions.length > 0 ? (
          <Grid container spacing={2}>
            {[...uniquePrescriptions]
              .filter(p => p && p.Date && !isNaN(new Date(p.Date)))
              .sort((a, b) => (convertToISO(b.Date) || '').localeCompare(convertToISO(a.Date) || ''))
              .map((prescription, index) => {
                const parsedDate = new Date(prescription.Date);
                const isMostRecent = index === 0;

                return (
                  <Grid item xs={12} sm={6} md={4} key={prescription?.id} sx={{ display: 'flex' }}>
                    <Card
                      elevation={0}
                      onClick={() => handleNavigate(prescription)}
                      sx={{
                        p: 2.5,
                        borderRadius: '16px',
                        border: '1px solid',
                        borderColor: isMostRecent ? '#DA6E0B' : '#eaebed',
                        backgroundColor: isMostRecent ? '#fff9f2' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          borderColor: '#DA6E0B',
                          boxShadow: '0 8px 24px rgba(218, 110, 11, 0.12)',
                          transform: 'translateY(-4px)'
                        }
                      }}
                    >
                      {isMostRecent && (
                        <Chip
                          label="Most Recent"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            backgroundColor: '#DA6E0B',
                            color: 'white',
                            fontSize: '10px',
                            height: '20px',
                            fontWeight: 700,
                            px: 0.5
                          }}
                        />
                      )}

                      <Stack direction="row" spacing={2.5} alignItems="flex-start" sx={{ flexGrow: 1 }}>
                        <Box sx={{
                          p: 1.8,
                          borderRadius: '10px',
                          backgroundColor: isMostRecent ? '#fbe9d7' : '#f0f2f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background-color 0.3s'
                        }}>
                          <img src="/icons/Rx.png" alt="Rx" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 800, fontSize: '15px', color: '#1A1C1E', mb: 0.5 }}>
                            ID: {prescription.id?.slice(-8).toUpperCase() || 'N/A'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6C757D', display: 'block', mb: 1.5, fontWeight: 500 }}>
                            {parsedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </Typography>

                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ color: '#6C757D', display: 'block', fontSize: '11px', mb: 0.2 }}>
                              Appointment ID
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#3B362A', mb: 1 }}>
                              {prescription.AppointmentID || 'Direct Prescription'}
                            </Typography>

                            <Typography variant="caption" sx={{ color: '#6C757D', display: 'block', fontSize: '11px', mb: 0.2 }}>
                              Prescribed By
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#DA6E0B' }}>
                              {prescription.Appointment_details?.doctor_name || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>

                      <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        {prescription.draftStatus ? (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{
                                textTransform: 'none',
                                color: '#DA6E0B',
                                borderColor: '#DA6E0B',
                                fontSize: '12px',
                                fontWeight: 600,
                                borderRadius: '6px',
                                px: 2,
                                '&:hover': { borderColor: '#c4620a', backgroundColor: 'rgba(218, 110, 11, 0.05)' }
                              }}
                            >
                              Edit Draft
                            </Button>
                            <IconButton
                              size="small"
                              color="error"
                              sx={{ backgroundColor: 'rgba(211, 47, 47, 0.05)', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' } }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDraftToDelete(prescription);
                                setOpenDialog(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          doctorDetails?.PID === prescription?.Doctor_details?.PID && (
                            <Button
                              size="small"
                              variant="contained"
                              sx={{
                                textTransform: "none",
                                backgroundColor: "#DA6E0B",
                                color: "white",
                                fontSize: '12px',
                                fontWeight: 600,
                                borderRadius: '6px',
                                px: 2.5,
                                py: 0.8,
                                boxShadow: 'none',
                                "&:hover": { backgroundColor: "#c4620a", boxShadow: '0 4px 12px rgba(218, 110, 11, 0.2)' },
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const updatedPrescription = { ...prescription };
                                delete updatedPrescription?.id;
                                delete updatedPrescription?.Follow_up;
                                setPrescription(updatedPrescription);
                                navigate("/prescription", { state: { isFollowUpEdit: true } });
                              }}
                            >
                              Follow up
                            </Button>
                          )
                        )}
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        ) : (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60vh',
            textAlign: 'center'
          }}>
            <img
              src="https://aiwellassets.s3.ap-south-1.amazonaws.com/no+prescription.png"
              alt="No prescription"
              style={{ width: '200px', opacity: 0.7 }}
            />
            <Typography variant="h6" sx={{ mt: 2, fontWeight: 600, color: '#3B362A' }}>
              No Prescriptions Found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              You haven't added any prescriptions for this patient yet.
            </Typography>
            <Button
              variant="outlined"
              sx={{
                textTransform: "none",
                borderColor: "#DA6E0B",
                color: "#DA6E0B",
                borderRadius: "8px",
                px: 4,
                "&:hover": { borderColor: "#c4620a", backgroundColor: 'rgba(218, 110, 11, 0.05)' }
              }}
              onClick={() => {
                setPrescription(null);
                navigate("/prescription");
              }}
            >
              Create New Prescription
            </Button>
          </Box>
        )}
      </Box>

      {/* Bottom Action Bar */}
      <Box sx={{
        p: 2,
        backgroundColor: 'white',
        boxShadow: '0 -4px 10px rgba(0,0,0,0.05)',
        display: 'flex',
        justifyContent: 'center',
        borderTop: '1px solid #e0e0e0',
        zIndex: 10
      }}>
        <Button
          variant="contained"
          sx={{
            maxWidth: '400px',
            width: '100%',
            textTransform: "none",
            backgroundColor: "#DA6E0B",
            color: "white",
            fontWeight: 600,
            py: 1.2,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(218, 110, 11, 0.3)',
            "&:hover": { backgroundColor: "#c4620a" },
          }}
          onClick={() => {
            setPrescription(null);
            navigate("/prescription");
          }}
        >
          + Create New Prescription
        </Button>
      </Box>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#fdfdfd",
            zIndex: 9999,
          }}
        >
          <div
            style={{ position: "relative", width: "10rem", height: "10rem" }}
          >
            <CircularProgress
              style={{ width: "100%", height: "100%", color: "#DA6E0B" }}
              thickness={1}
            />
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={loadingimg}
                style={{ width: "80%", height: "80%" }}
                alt="Loading"
              />
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '16px',
            fontFamily: "'Poppins', sans-serif",
          },
        }}
      >
        <DialogTitle
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#DA6E0B',
          }}
        >
          Delete Draft?
        </DialogTitle>

        <DialogContent
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 500,
            fontSize: '1rem',
            color: '#3B362A',
          }}
        >
          Are you sure you want to delete this draft prescription? This action cannot be undone.
        </DialogContent>

        <DialogActions style={{ padding: '12px 24px' }}>
          <Button
            onClick={() => setOpenDialog(false)}
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteDraftById(draftToDelete?.id, draftToDelete?.PatientID, draftToDelete?.AppointmentID)}
            variant="contained"
            color="error"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
}

export default Prescription;
